// server/services/weeklyLoad.js
//
// Weekly Load — ONE definition for every surface (sense-check 2026-09-19 H9).
// Server metrics (Goal Likelihood, RESET Score, adaptation R5 via buildKpis)
// and the client WK Load card / Today card all call weeklyLoadToDate().
//
// Methodology:
//   §Pillar 5 rule 5 — "this week" metrics compare against PLAN-TO-DATE:
//     days before today are due; today's planned km count once a run for
//     today is logged; later days are not due yet.
//   §Adaptation Rules R5 — weekly load > 120 % of plan = over.
//
// Hikes/walks count only when the athlete opted in (includeHikes), at full
// km — the same rule the user baseline uses (§"User baseline").
//
// src/lib/weeklyLoad.js is a BYTE-IDENTICAL client mirror (checked by
// server/test/mirror.test.js). CommonJS, no object spread (CLAUDE.md
// §"Generator mirror"). Edit this file, then:
//   cp server/services/weeklyLoad.js src/lib/weeklyLoad.js

const RUN_TYPES = new Set(['Run', 'TrailRun', 'Treadmill', 'VirtualRun']);
const HIKE_TYPES = new Set(['Hike', 'Walk']);
// Bands: under 70 % = under, 70–120 % = on track, above 120 % = over (R5).
const OVER_RATIO = 1.20;
const ON_TRACK_RATIO = 0.70;

function pad2(n) { return String(n).padStart(2, '0'); }
function localIso(d) { return d.getFullYear() + '-' + pad2(d.getMonth() + 1) + '-' + pad2(d.getDate()); }

/** Monday..Sunday ISO bounds of the week containing `now` (runtime local time). */
function weekBounds(now) {
  const d = new Date(now);
  d.setHours(0, 0, 0, 0);
  const dow = d.getDay();
  const monday = new Date(d);
  monday.setDate(d.getDate() + (dow === 0 ? -6 : 1 - dow));
  const nextMonday = new Date(monday);
  nextMonday.setDate(monday.getDate() + 7);
  return { startIso: localIso(monday), endIso: localIso(nextMonday), todayIso: localIso(d) };
}

function activityIso(a) {
  const raw = a && (a.start_date || a.date);
  if (!raw) return null;
  const d = new Date(raw);
  return isNaN(+d) ? null : localIso(d);
}

function countsAsVolume(a, includeHikes) {
  const t = (a && a.type) || 'Run';
  if (RUN_TYPES.has(t)) return true;
  return !!includeHikes && HIKE_TYPES.has(t);
}

/**
 * @param {Object} p
 * @param {Array}  p.plannedSessions  [{ date:'YYYY-MM-DD', plannedKm }]
 * @param {Array}  p.activities       [{ type, start_date, distance_m }]
 * @param {Date|number} [p.now]
 * @param {boolean} [p.includeHikes]
 * @returns {{ plannedWeekKm, plannedToDateKm, actualKm, deltaToDateKm, ratio, percentage, status, hasPlan }}
 *   status: 'no-plan' | 'not-due' | 'under' | 'on-track' | 'over'
 */
function weeklyLoadToDate(p) {
  const opts = p || {};
  const b = weekBounds(opts.now != null ? opts.now : Date.now());
  const inWeek = (iso) => iso != null && iso >= b.startIso && iso < b.endIso;

  let actualKm = 0;
  let hasRunToday = false;
  for (const a of opts.activities || []) {
    if (!countsAsVolume(a, opts.includeHikes)) continue;
    const iso = activityIso(a);
    if (!inWeek(iso)) continue;
    actualKm += (Number(a.distance_m) || 0) / 1000;
    if (iso === b.todayIso) hasRunToday = true;
  }

  let plannedWeekKm = 0;
  let plannedToDateKm = 0;
  let hasPlan = false;
  for (const s of opts.plannedSessions || []) {
    const iso = s && typeof s.date === 'string' ? s.date.slice(0, 10) : null;
    if (!inWeek(iso)) continue;
    hasPlan = true;
    // Duration-only sessions (time-on-feet, hike) prescribe time, not
    // distance, so they carry plannedKm: null and an `estimatedKm` derived
    // from the plan's own threshold pace (§Pillar 4, planGenerator.js).
    // Counting only plannedKm omitted the biggest session of a 3-session
    // Specific week: the athlete ran exactly the plan and Weekly Load called
    // it 194 % of target. Same rule as the week total — estimate, or nothing.
    const km = Number(s.plannedKm) || Number(s.estimatedKm) || 0;
    if (km <= 0) continue;
    plannedWeekKm += km;
    if (iso < b.todayIso || (iso === b.todayIso && hasRunToday)) plannedToDateKm += km;
  }

  const r1 = (x) => Math.round(x * 10) / 10;
  const out = {
    plannedWeekKm: r1(plannedWeekKm),
    plannedToDateKm: r1(plannedToDateKm),
    actualKm: r1(actualKm),
    deltaToDateKm: r1(actualKm - plannedToDateKm),
    ratio: null,
    percentage: null,
    status: 'no-plan',
    hasPlan: hasPlan && plannedWeekKm > 0,
  };
  if (!out.hasPlan) return out;
  if (plannedToDateKm <= 0) {
    // Nothing due yet this week. Running ahead of the plan still counts
    // against the full week so an early overshoot is visible.
    out.status = actualKm > plannedWeekKm * OVER_RATIO ? 'over' : 'not-due';
    return out;
  }
  const ratio = actualKm / plannedToDateKm;
  out.ratio = Math.round(ratio * 100) / 100;
  out.percentage = Math.round(ratio * 100);
  out.status = ratio > OVER_RATIO ? 'over'
    : ratio >= ON_TRACK_RATIO ? 'on-track'
    : 'under';
  return out;
}

/**
 * Card verdict for a weeklyLoadToDate() result — one mapping for every card.
 * tone: 'neutral' | 'ontrack' | 'warning' | 'off'.
 */
function weeklyLoadVerdict(wl) {
  if (!wl || !wl.hasPlan) return { tone: 'neutral', label: '—' };
  const d = wl.deltaToDateKm;
  const signed = (d > 0 ? '+' : '') + d + ' km';
  if (wl.status === 'not-due') return { tone: 'ontrack', label: 'Nothing due yet' };
  if (wl.status === 'on-track') return { tone: 'ontrack', label: 'On track' };
  if (wl.status === 'under') return { tone: 'off', label: 'Off track · ' + signed };
  if (wl.status === 'over') return { tone: 'warning', label: 'Over plan · ' + signed };
  return { tone: 'neutral', label: '—' };
}

module.exports = {
  weeklyLoadToDate,
  weeklyLoadVerdict,
  weekBounds,
  OVER_RATIO,
  ON_TRACK_RATIO,
};
